// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    getProductInfosByProductIdForVariantChild,
    getProductVariantChild,
} from "@west/App/Store/Selectors/ProductListSelectors";

type Parameter = {
    unitOfMeasure: string;
    productId: string;
    parentProductId: string;
};

type HandlerType = Handler<Parameter>;

export const DispatchChangeUnitOfMeasure: HandlerType = props => {
    props.dispatch({
        type: "xProductListVariantChildren/UpdateProductInfo",
        productId: props.parameter.productId,
        parentProductId: props.parameter.parentProductId,
        unitOfMeasure: props.parameter.unitOfMeasure,
    } as any);
};

export const UpdatePrice: HandlerType = props => {
    const state = props.getState();
    const { productId, unitOfMeasure } = props.parameter;
    const product = getProductVariantChild(state, props.parameter.parentProductId);
    if (!product || product.quoteRequired) {
        return;
    }

    const productInfo = getProductInfosByProductIdForVariantChild(state, props.parameter.parentProductId);
    if (!productInfo) {
        return;
    }

    props.dispatch({
        type: "xProductListVariantChildren/LoadingRealTimePricing",
        parentProductId: props.parameter.parentProductId,
        productId,
    } as any);

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: [
                {
                    productId,
                    qtyOrdered: productInfo.qtyOrdered,
                    unitOfMeasure: unitOfMeasure ?? "",
                },
            ],
            onSuccess: realTimePricing => {
                const pricing = realTimePricing.realTimePricingResults!.find(o => o.productId === productId);
                if (pricing) {
                    props.dispatch({
                        type: "xProductListVariantChildren/CompleteLoadRealTimePricing",
                        realTimePricing,
                        parentProductId: props.parameter.parentProductId,
                    } as any);
                } else {
                    props.dispatch({
                        type: "xProductListVariantChildren/FailedLoadRealTimePricing",
                        parentProductId: props.parameter.parentProductId,
                    } as any);
                }
            },
            onError: () => {
                props.dispatch({
                    type: "xProductListVariantChildren/FailedLoadRealTimePricing",
                    parentProductId: props.parameter.parentProductId,
                } as any);
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );
};

export const chain = [DispatchChangeUnitOfMeasure, UpdatePrice];

const changeUnitOfMeasureForChild = createHandlerChainRunner(chain, "ChangeUnitOfMeasureForChild");
export default changeUnitOfMeasureForChild;
